import React, {
  ReactElement,
  useEffect,
  useState,
  Fragment,
  useCallback,
} from 'react';
import { SchemaWithClient } from '../../types/SchemaWithClient';
import { Entity } from '../../types/entities/Entity';
import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Button as MUIButton,
  Checkbox,
  DialogContentText,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';

import api from '../../services/api';
import { toaster } from '../../services/toaster';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import { SlidingPaneEdit } from '../Page/SlidingPaneEdit';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { file } from '../../schema/file';
import DialogContent from '@mui/material/DialogContent';
import { FileUploader } from 'react-drag-drop-files';
import Zoom from 'react-medium-image-zoom';
import 'react-medium-image-zoom/dist/styles.css';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import Tooltip from "@mui/material/Tooltip";

type Props<T extends Entity> = {
  schema: SchemaWithClient<T>;
};

export const ServiceProviderImages = ({
  schema,
  values,
  tableType,
}: {
  schema: any;
  values: any;
  tableType: string;
}): ReactElement => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState<any>(false);
  const [properties, setProperties] = useState<any>([]);
  const [openedSlidingPane, setOpenedSlidingPane] = useState<any>({});
  const [addingEntity, setAddingEntity] = useState<boolean>(false);
  const [files, setFiles] = useState<any>([]);

  const fetchImages = useCallback(() => {
    const searchParams = new URLSearchParams({
      service_provider: values?.id,
    });

    const prices = api
      .get('/service-provider-images', searchParams)
      .then((res: any) => {
        setProperties(res?.images);
      });
  }, []);

  const uploadFiles = () => {
    // Create a form and post it to server
    let formData = new FormData();
    const salesPartnerRoles: any = [];
    const extraData: any = [];
    files.forEach((file: any, i: any) => {
      salesPartnerRoles.push({
        sales_partner_visible: file?.sales_partner_visible,
        sales_partner_use: file?.sales_partner_use,
      });
      extraData.push({
        title: file?.title,
        caption: file?.caption,
        description: file?.description,
      });
      formData.append(`file-${i}`, file);
    });

    formData.append(`column`, 'images');
    formData.append(`id`, values?.id);
    formData.append(`schema`, JSON.stringify(schema));
    formData.append(`salesPartnerRoles`, JSON.stringify(salesPartnerRoles));
    formData.append(`extraData`, JSON.stringify(extraData));

    const allFiles = [...files];
    api
      .postFile(`/upload-files`, formData)
      .then((res: any) => {
        res.map((item: any) => {
          allFiles.push(item);
        });

        setAddingEntity(true);
        setFiles([]);
        fetchImages();
      })
      .catch((error: any) => {});
  };

  const downloadImage = (response: any, fileName: string) => {
    const imageBlob = new Blob([response], { type: response?.type });
    const imageUrl = URL.createObjectURL(imageBlob);
    const link: any = document.createElement('a');
    link.href = imageUrl;
    link.download = fileName ? fileName : 'image.png';
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const handleDownload = (attachment: any) => {
    const searchParams = new URLSearchParams({
      fileName: attachment?.name,
      path: 'public',
    });
    api
      .getFile(`/download-file`, searchParams)
      .then((res: any) => {
        downloadImage(res, attachment?.name);
      })
      .catch((error) => {
        console.log(error);
        toaster.show({
          icon: 'error',
          message: `Error while download data`,
          intent: 'danger',
        });
      });

    return true;
  };

  const handleChangeCheckBox = (event: any, index: any, columnName: string) => {
    const newFiles = [...files];
    newFiles.map((row: any, rowIndex: number) => {
      if (index === rowIndex) {
        row[columnName] = event.target.checked;
      }
      newFiles[rowIndex] = row;
    });
    setFiles(newFiles);
  };

  const handleChange = (newFiles: any) => {
    const newFilesArray = [...newFiles];
    const oldFiles = [...files];
    newFilesArray.map((file: any) => {
      file.preview = URL.createObjectURL(file);
      oldFiles.push(file);
    });
    setFiles(oldFiles);
  };

  const handleDeleteFile = (index: any) => {
    const newFiles = files.filter((row: any, rowIndex: number) => {
      return index !== rowIndex;
    });

    setFiles(newFiles);
  };

  const handleChangeData = (
    event: any,
    index: any,
    columnName: string,
    value: any
  ) => {
    const newFiles = [...files];
    newFiles.map((row: any, rowIndex: number) => {
      if (index === rowIndex) {
        row[columnName] = value;
      }
      newFiles[rowIndex] = row;
    });
    setFiles(newFiles);
  };

  const handleUpdateImage = useCallback(
    async (image: any, data: any) => {
      file.client
        .update(image?.id, data)
        .then((res: any) => {
          const oldProperties = [...properties];
          const newRows = oldProperties.map((row: any) => {
            return row.id === image.id ? res : row;
          });
          setProperties(newRows);
        })
        .catch((e: any) => {
          console.error(e);
        });
    },
    [properties]
  );

  useEffect(() => {
    fetchImages();
  }, [values, schema]);

  return (
    <>
      <Stack
        direction={'row'}
        alignContent={'center'}
        gap={2}
        sx={{
          marginY: '20px',
        }}
      >
        <Stack direction={'row'}>
          <Button
            variant="contained"
            onClick={() => {
              setAddingEntity(true);
            }}
          >
            Upload
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={2}>
        {properties.map((image: any) => {
          return (
            <Grid item xs={6} lg={4}>
              <Card sx={{}}>
                <Zoom>
                  <CardMedia
                    image={image?.link}
                    title={image?.name}
                    sx={{
                      backgroundSize: 'contain',
                      height: {
                        lg: 300,
                        xl: 200,
                      },
                    }}
                  ></CardMedia>
                </Zoom>
                <CardContent>
                  <Stack
                    direction={'row'}
                    alignContent={'center'}
                    justifyContent={'center'}
                    gap={0}
                  >
                    <IconButton
                      aria-label="Edit"
                      onClick={() => {
                        setOpenedSlidingPane({
                          schema: file,
                          row: image,
                        });
                      }}
                    >
                      <EditIcon color={'primary'} />
                    </IconButton>
                    <IconButton
                      onClick={() => {
                        handleDownload(image);
                      }}
                    >
                      <CloudDownloadRoundedIcon color={'primary'} />
                    </IconButton>
                    <IconButton
                      aria-label="Sales partner visible"
                      onClick={() => {
                        handleUpdateImage(image, {
                          sales_partner_visible: !image?.sales_partner_visible,
                        });
                      }}
                    >
                      {image?.sales_partner_visible ? (
                        <VisibilityOutlinedIcon color={'primary'} />
                      ) : (
                        <VisibilityOffOutlinedIcon color={'disabled'} />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="Sales partner use"
                      onClick={() => {
                        handleUpdateImage(image, {
                          sales_partner_use: !image?.sales_partner_use,
                        });
                      }}
                    >
                      {image?.sales_partner_use ? (
                        <LockOpenIcon color={'primary'} />
                      ) : (
                        <LockOutlinedIcon color={'disabled'} />
                      )}
                    </IconButton>
                    <IconButton
                      aria-label="Delete"
                      onClick={() => {
                        setOpenConfirmDialog({
                          row: image,
                          schema: file,
                        });
                      }}
                    >
                      <DeleteIcon color={'error'} />
                    </IconButton>
                  </Stack>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign={'center'}
                  >
                    {image?.caption}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {addingEntity && (
        <Dialog
          open={addingEntity}
          onClose={() => {
            setAddingEntity(false);
          }}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">
            <Stack
              direction={'row'}
              alignContent={'center'}
              justifyContent={'space-between'}
            >
              <span>Upload Picture</span>
              <IconButton
                color={'default'}
                size={'small'}
                onClick={() => {
                  setAddingEntity(false);
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <FileUploader
                multiple={true}
                handleChange={handleChange}
                name="file"
                classes={'w-full !max-w-[100%]'}
              />
              <Divider />

              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-start"
                marginBottom={2}
                alignItems={'center'}
              >
                <div className="multiselect-field w-full">
                  <Stack
                    direction="column"
                    spacing={1}
                    justifyContent="flex-start"
                    mt={1}
                  >
                    {files.map((file: any, index: any) => {
                      return (
                        <>
                          <Stack
                            className={'bg-[#f9f9f9]'}
                            direction="row"
                            spacing={1}
                            justifyContent="space-between"
                            mt={2}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems={'center'}
                              mt={2}
                            >
                              <img
                                src={file.preview}
                                className={`w-[50px] h-[50px] object-cover`}
                                alt={`preview ${index}`}
                              />
                              <Stack direction="column" spacing={0}>
                                <span>{file?.name}</span>
                                <small>
                                  {(file.size / 1024 / 1024).toFixed(2)} MB
                                </small>
                              </Stack>
                            </Stack>

                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems={'center'}
                              mt={2}
                            >
                              <Tooltip title="Is it visible to sales partners?">
                                <Checkbox
                                  checked={
                                    file?.sales_partner_visible
                                      ? file.sales_partner_visible
                                      : false
                                  }
                                  inputProps={{ 'aria-label': 'Checkbox demo' }}
                                  icon={<VisibilityOffOutlinedIcon />}
                                  checkedIcon={
                                    <VisibilityOutlinedIcon color={'primary'} />
                                  }
                                  onChange={(event) => {
                                    handleChangeCheckBox(
                                      event,
                                      index,
                                      'sales_partner_visible'
                                    );
                                  }}
                                />
                              </Tooltip>
                              <Tooltip title="Can sales partners use it?">
                                <Checkbox
                                  checked={
                                    file?.sales_partner_use
                                      ? file.sales_partner_use
                                      : false
                                  }
                                  inputProps={{ 'aria-label': 'Checkbox demo' }}
                                  icon={<LockOutlinedIcon />}
                                  checkedIcon={
                                    <LockOpenIcon color={'primary'} />
                                  }
                                  onChange={(event) => {
                                    handleChangeCheckBox(
                                      event,
                                      index,
                                      'sales_partner_use'
                                    );
                                  }}
                                />
                              </Tooltip>

                              <IconButton
                                aria-label="delete"
                                size="small"
                                color={'error'}
                                onClick={() => {
                                  handleDeleteFile(index);
                                }}
                                sx={{
                                  padding: '9px',
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </Stack>
                          <Stack
                            className={'bg-[#f9f9f9]'}
                            direction="row"
                            spacing={1}
                            sx={{
                              marginTop: '0px !important',
                              paddingTop: '5px !important',
                            }}
                          >
                            <TextField
                              label="Tile"
                              size="small"
                              fullWidth={true}
                              onChange={(event) => {
                                handleChangeData(
                                  event,
                                  index,
                                  'title',
                                  event?.target.value
                                );
                              }}
                            />
                            <TextField
                              fullWidth={true}
                              label="Caption"
                              size="small"
                              onChange={(event) => {
                                handleChangeData(
                                  event,
                                  index,
                                  'caption',
                                  event?.target.value
                                );
                              }}
                            />
                          </Stack>
                          <Stack
                            className={'bg-[#f9f9f9]'}
                            direction="row"
                            spacing={1}
                            sx={{
                              marginTop: '0px !important',
                              paddingTop: '5px !important',
                            }}
                          >
                            <TextField
                              label="Description"
                              size="small"
                              fullWidth={true}
                              onChange={(event) => {
                                handleChangeData(
                                  event,
                                  index,
                                  'description',
                                  event?.target.value
                                );
                              }}
                            />
                          </Stack>
                        </>
                      );
                    })}
                  </Stack>
                </div>
              </Stack>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={uploadFiles}>Upload Files and Close</Button>
          </DialogActions>
        </Dialog>
      )}

      <Dialog
        open={openConfirmDialog}
        onClose={() => {
          setOpenConfirmDialog(false);
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {'Are you sure you want to delete it?'}
        </DialogTitle>
        <DialogActions>
          <MUIButton
            onClick={() => {
              setOpenConfirmDialog(false);
            }}
          >
            Cancel
          </MUIButton>
          <MUIButton
            variant={'contained'}
            color={'error'}
            onClick={() => {
              const deletedRow = openConfirmDialog?.row;

              openConfirmDialog?.schema.client
                .delete(deletedRow.id as number, { id: deletedRow.id })
                .then((res: any) => {
                  const tempProperties = [...properties];
                  const newRows = tempProperties.filter((item: any) => {
                    return item?.id !== deletedRow?.id;
                  });
                  setProperties(newRows);
                  toaster.show({
                    intent: 'success',
                    message: `Successfully deleted ${schema.name}: ${
                      deletedRow?.name ? deletedRow?.name : deletedRow?.id
                    }`,
                  });
                  setOpenConfirmDialog(false);
                })
                .catch((e: any) => {
                  console.log(e);
                  toaster.show({
                    icon: 'error',
                    message: `Error: Unable to delete Image: ${
                      deletedRow?.name ? deletedRow?.name : deletedRow?.id
                    }`,
                    intent: 'danger',
                  });
                });
            }}
            autoFocus
          >
            Delete
          </MUIButton>
        </DialogActions>
      </Dialog>

      {openedSlidingPane?.row?.id && (
        <SlidingPaneEdit
          schema={openedSlidingPane?.schema}
          id={openedSlidingPane?.row?.id}
          onSubmit={() => {
            fetchImages();
            setOpenedSlidingPane({});
          }}
          propertiesFromTable={
            openedSlidingPane?.row ? openedSlidingPane?.row : {}
          }
          onClose={(value) => {
            setOpenedSlidingPane({});
          }}
        />
      )}
    </>
  );
};
